/**
 * DatabaseConfigDrawer — per-row editor for a single server database config item.
 *
 * Edits ONLY the `config_value` of the selected core_config row. The input is
 * validated against the row's `data_type`:
 *   - int   → optional sign + whole number      (^-?\d+$)
 *   - float → optional sign + decimal / number   (^-?\d*\.?\d+$)
 *   - else  → any standard printable ASCII char  (^[\x20-\x7E]*$)
 *
 * Saves via the shared core_config path: emits a single-item array of
 * { config_value, config_id, modified_by } to onSave (handled by
 * ServerProfilePage.handleDbConfigSave → saveServerConfig).
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Drawer from '../../../../components/v2/Drawer.jsx';

/**
 * Map a data_type string to a validator descriptor. Matching is
 * case-insensitive; int/integer and float/double/decimal/number/numeric map to
 * the numeric buckets. Anything else (incl. unknown/future types) is treated as
 * a free-text string so the drawer keeps working for new data_type values.
 */
function getValidatorForType(dataType) {
  const t = (dataType || '').toString().trim().toLowerCase();
  if (t === 'int' || t === 'integer') {
    return {
      type: 'int',
      regex: /^-?\d+$/,
      inputMode: 'numeric',
      hint: 'Whole number — negatives allowed (e.g. 30, -5).',
      error: 'Value must be a whole number.',
    };
  }
  if (['float', 'double', 'decimal', 'number', 'numeric'].includes(t)) {
    return {
      type: 'float',
      regex: /^-?\d*\.?\d+$/,
      inputMode: 'decimal',
      hint: 'Numeric value — decimals and negatives allowed (e.g. 2.5, -0.3).',
      error: 'Value must be a number.',
    };
  }
  return {
    type: 'str',
    regex: /^[\x20-\x7E]*$/,
    inputMode: 'text',
    hint: 'Any standard printable characters.',
    error: 'Only standard printable characters are allowed.',
  };
}

const DatabaseConfigDrawer = ({ open, onClose, onSave, saving, row, userId }) => {
  const [value, setValue] = useState('');

  const validator = useMemo(() => getValidatorForType(row?.data_type), [row]);

  // Seed the field from the row only on the closed → open transition, so the
  // 5s realtime poll refreshing configData can't clobber an in-progress edit.
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setValue(row?.config_value ?? '');
    }
    prevOpen.current = open;
  }, [open, row]);

  // Numeric types require a value; strings may be empty (e.g. a blank delimiter).
  const isEmpty = value.trim() === '';
  const isValid = validator.type === 'str'
    ? validator.regex.test(value)
    : (!isEmpty && validator.regex.test(value.trim()));

  // Show the error only once the user has typed something invalid (don't yell
  // at an empty numeric field before they start).
  const showError = !isValid && !isEmpty;

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!isValid || !row?.config_id) return;
    const out = validator.type === 'str' ? value : value.trim();
    onSave([{ config_value: out, config_id: row.config_id, modified_by: userId }]);
  }, [isValid, row, validator, value, onSave, userId]);

  return (
    <Drawer
        open={open}
        onClose={onClose}
        title="Edit Config Value"
        backdrop={false}
        bodyAs="form"
        bodyProps={{ onSubmit: handleSubmit }}
        footer={(
            <>
                <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="dp-btn dp-btn--primary" disabled={saving || !isValid}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
            </>
        )}
    >
        {/* Technician caution — these are low-level core_config values. */}
        <div className="sp-config-warning" role="alert">
          <svg className="sp-config-warning__icon dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <span>
            <strong>Caution:</strong> change this value only if you know the supported range
            for this setting. An unsupported value can have catastrophic effects on this
            server — including preventing alerts from being generated.
          </span>
        </div>

        {/* Read-only context for the selected row */}
        <div className="dp-drawer__field">
          <span className="dp-drawer__label">Config Name</span>
          <p className="sp-config-readonly sp-config-readonly--muted">{row?.config_name || '—'}</p>
        </div>
        <div className="dp-drawer__field">
          <span className="dp-drawer__label">Data Type</span>
          <p className="sp-config-readonly sp-config-readonly--muted">{row?.data_type || '—'}</p>
        </div>
        {row?.description ? (
          <div className="dp-drawer__field">
            <span className="dp-drawer__label">Description</span>
            <p className="sp-config-readonly sp-config-readonly--muted">{row.description}</p>
          </div>
        ) : null}

        {/* Editable value */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label" htmlFor="sp-config-value">
            Value <span className="dp-drawer__required">*</span>
          </label>
          <input
            id="sp-config-value"
            className={`dp-drawer__input${showError ? ' is-invalid' : ''}`}
            value={value}
            inputMode={validator.inputMode}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="off"
            autoFocus
          />
          {showError ? (
            <p className="dp-drawer__error">{validator.error}</p>
          ) : (
            <p className="sp-config-hint">{validator.hint}</p>
          )}
        </div>

        {/* Footer */}
    </Drawer>
  );
};

export default DatabaseConfigDrawer;
